// ─── My First Project — Auto-seed Hook ───
//
// Runs once per user (per browser) after auth is ready and an instance ID
// is selected. Inserts a sample project + tasks flagged is_sample=true so
// they remain invisible to org/instance analytics, dashboards, and rollups.
//
// Guards:
//  - localStorage flag (mfp_seeded_<userId>_<instanceId>) prevents repeated
//    attempts in the same browser.
//  - DB check looks for any existing sample project owned by this user in
//    the current instance, so a fresh browser does not duplicate the seed.
//
// Also exports `seedMyFirstProject` and `resetMyFirstProject` for the
// "Reset sample project" UI control.

import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthReady } from '@/hooks/useAuthReady';
import { useInstance } from '@/contexts/InstanceContext';
import {
  MY_FIRST_PROJECT_NAME,
  MY_FIRST_PROJECT_TASKS,
  type SeedSampleTask,
} from '@/data/myFirstProjectSeed';

const lsKey = (userId: string, instanceId: string) =>
  `mfp_seeded_${userId}_${instanceId}`;

/** Next Monday at 00:00 local time (or today @ 00:00 if today is Monday). */
function nextMondayMorning(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  const addDays = dow === 1 ? 0 : (8 - dow) % 7;
  d.setDate(d.getDate() + addDays);
  return d;
}

/** End boundary for a daily-mode task: start + ceil(days) calendar days.
 *  Start is assumed to be a workday and durationDays <= 5, so end falls on
 *  Sat 00:00 at the latest — never bleeds into the following weekend's work. */
function addCalendarDays(base: Date, days: number): Date {
  const result = new Date(base);
  result.setDate(result.getDate() + Math.max(1, Math.ceil(days)));
  return result;
}

function buildActionItems(task: SeedSampleTask) {
  return task.actionItems.map((ai, i) => ({
    id: `${task.key}-ai-${i + 1}`,
    text: ai.text,
    completed: false,
  }));
}

/**
 * Insert the sample project + tasks for `userId` in `instanceId`.
 * Idempotent unless `force=true` (caller is responsible for prior cleanup).
 * Returns the new project id, or null if skipped/failed.
 */
export async function seedMyFirstProject(
  userId: string,
  instanceId: string,
  opts: { force?: boolean } = {}
): Promise<string | null> {
  try {
    if (!opts.force) {
      const existing = await supabase
        .from('projects')
        .select('id')
        .eq('instance_id', instanceId)
        .eq('user_id', userId)
        .eq('is_sample', true)
        .limit(1)
        .maybeSingle();
      if (existing.data) {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(lsKey(userId, instanceId), '1');
        }
        return existing.data.id as string;
      }
    }

    // Only members who can write in this workspace may create projects (same
    // rule the database enforces). Others simply don't get a sample project,
    // instead of sending an insert the database will refuse.
    const { data: canWrite, error: canWriteErr } = await supabase.rpc('can_write_in_instance' as any, {
      _user_id: userId,
      _instance_id: instanceId,
    } as any);
    if (canWriteErr || canWrite !== true) return null;

    const { data: project, error: projErr } = await supabase
      .from('projects')
      .insert({
        name: MY_FIRST_PROJECT_NAME,
        user_id: userId,
        instance_id: instanceId,
        is_sample: true,
      } as any)
      .select('id')
      .single();

    if (projErr || !project) {
      // 23505 = unique_violation. The DB enforces one sample project per
      // (user, instance), so a concurrent login from another browser may have
      // beaten us to the insert. Treat that as success and re-fetch the row.
      if ((projErr as any)?.code === '23505') {
        const winner = await supabase
          .from('projects')
          .select('id')
          .eq('instance_id', instanceId)
          .eq('user_id', userId)
          .eq('is_sample', true)
          .limit(1)
          .maybeSingle();
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(lsKey(userId, instanceId), '1');
        }
        return (winner.data?.id as string) ?? null;
      }
      console.warn('[mfp] Could not create sample project:', projErr);
      return null;
    }

    const projectStart = nextMondayMorning();
    const keyToId = new Map<string, string>();

    // Insert level-by-level so a child's parent is always already in keyToId.
    const byKey = new Map(MY_FIRST_PROJECT_TASKS.map((t) => [t.key, t]));
    const levelOf = (t: SeedSampleTask): number => {
      let depth = 0;
      let cur: SeedSampleTask | undefined = t;
      while (cur?.parentKey) {
        depth += 1;
        cur = byKey.get(cur.parentKey);
        if (depth > 10) break;
      }
      return depth;
    };
    const levels = new Map<number, SeedSampleTask[]>();
    for (const t of MY_FIRST_PROJECT_TASKS) {
      const lvl = levelOf(t);
      if (!levels.has(lvl)) levels.set(lvl, []);
      levels.get(lvl)!.push(t);
    }

    const insertGroup = async (group: SeedSampleTask[]) => {
      const rows = group.map((t) => {
        const startOffset = Math.max(0, Math.floor(t.startOffsetDays ?? 0));
        const startTime = new Date(projectStart);
        startTime.setDate(startTime.getDate() + startOffset);
        const endTime = addCalendarDays(startTime, t.durationDays);
        const parentId = t.parentKey ? keyToId.get(t.parentKey) ?? null : null;
        return {
          project_id: project.id,
          name: t.name,
          intent: t.intent,
          specs: t.specs ?? [],
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          schedule_mode: 'daily',
          parent_id: parentId,
          assignees: [userId],
          assignee: userId,
          action_items: buildActionItems(t),
          status: 'active',
          progress: 0,
          is_sample: true,
        };
      });

      const { data: inserted, error } = await supabase
        .from('tasks')
        .insert(rows as any)
        .select('id, name');

      if (error || !inserted) {
        console.warn('[mfp] Could not insert sample tasks:', error);
        return false;
      }
      inserted.forEach((row: any) => {
        const seed = group.find((t) => t.name === row.name);
        if (seed) keyToId.set(seed.key, row.id);
      });
      return true;
    };

    const sortedLevels = Array.from(levels.keys()).sort((a, b) => a - b);
    for (const lvl of sortedLevels) {
      const ok = await insertGroup(levels.get(lvl)!);
      if (!ok) break;
    }

    // Insert FS dependencies for any task with predecessorKey
    const depRows = MY_FIRST_PROJECT_TASKS
      .filter((t) => !!t.predecessorKey)
      .map((t) => {
        const fromId = keyToId.get(t.predecessorKey!);
        const toId = keyToId.get(t.key);
        if (!fromId || !toId) return null;
        return {
          project_id: project.id,
          from_task_id: fromId,
          to_task_id: toId,
          type: 'finish-to-start',
          lag_minutes: 0,
        };
      })
      .filter(Boolean);
    if (depRows.length > 0) {
      const { error: depErr } = await supabase
        .from('dependencies')
        .insert(depRows as any);
      if (depErr) console.warn('[mfp] Could not insert sample dependencies:', depErr);
    }

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(lsKey(userId, instanceId), '1');
    }
    return project.id as string;
  } catch (err) {
    console.warn('[mfp] Seed failed:', err);
    return null;
  }
}

/**
 * Delete every sample project (and cascading tasks) owned by `userId` in
 * `instanceId`, then re-seed a fresh copy. Used by the "Reset sample project"
 * button. Returns the new project id or null on failure.
 */
export async function resetMyFirstProject(
  userId: string,
  instanceId: string
): Promise<string | null> {
  try {
    // tasks.project_id has ON DELETE CASCADE, so deleting projects is enough.
    const { error: delErr } = await supabase
      .from('projects')
      .delete()
      .eq('user_id', userId)
      .eq('instance_id', instanceId)
      .eq('is_sample', true);

    if (delErr) {
      console.warn('[mfp] Reset delete failed:', delErr);
      return null;
    }

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(lsKey(userId, instanceId));
    }

    return await seedMyFirstProject(userId, instanceId, { force: true });
  } catch (err) {
    console.warn('[mfp] Reset failed:', err);
    return null;
  }
}

async function backfillSampleDaily(userId: string, instanceId: string) {
  try {
    const { data: projects } = await supabase
      .from('projects')
      .select('id')
      .eq('instance_id', instanceId)
      .eq('user_id', userId)
      .eq('is_sample', true);
    const ids = (projects ?? []).map((p: any) => p.id);
    if (ids.length === 0) return;
    await supabase
      .from('tasks')
      .update({ schedule_mode: 'daily' } as any)
      .in('project_id', ids)
      .neq('schedule_mode', 'daily');
  } catch (err) {
    console.warn('[mfp] Daily backfill failed:', err);
  }
}

async function backfillSampleDayChain(userId: string, instanceId: string) {
  try {
    const { data: projects } = await supabase
      .from('projects')
      .select('id')
      .eq('instance_id', instanceId)
      .eq('user_id', userId)
      .eq('is_sample', true);
    const projectIds = (projects ?? []).map((p: any) => p.id);
    if (projectIds.length === 0) return;

    for (const pid of projectIds) {
      // Only top-level day tasks (parent_id IS NULL), in order Day1..Day5.
      const { data: dayTasks } = await supabase
        .from('tasks')
        .select('id, name, parent_id')
        .eq('project_id', pid)
        .is('parent_id', null);
      if (!dayTasks || dayTasks.length < 2) continue;

      const ordered = dayTasks
        .filter((t: any) => /^Day\s*\d+/i.test(t.name))
        .sort((a: any, b: any) => {
          const na = parseInt(a.name.match(/\d+/)?.[0] ?? '0', 10);
          const nb = parseInt(b.name.match(/\d+/)?.[0] ?? '0', 10);
          return na - nb;
        });
      if (ordered.length < 2) continue;

      const rows = [];
      for (let i = 0; i < ordered.length - 1; i++) {
        rows.push({
          project_id: pid,
          from_task_id: ordered[i].id,
          to_task_id: ordered[i + 1].id,
          type: 'finish-to-start',
          lag_minutes: 0,
        });
      }
      // Upsert-style: insert and ignore unique-violation duplicates.
      const { error } = await supabase.from('dependencies').insert(rows as any);
      if (error && (error as any).code !== '23505') {
        console.warn('[mfp] Day chain backfill insert failed:', error);
      }
    }
  } catch (err) {
    console.warn('[mfp] Day chain backfill failed:', err);
  }
}

export function useFirstProjectSeed(opts: { enabled?: boolean } = {}) {
  const enabled = opts.enabled ?? true;
  const { user, isReady } = useAuthReady();
  const { currentInstanceId, instancesLoaded } = useInstance();
  // Track which (user, instance) pairs have been seeded in this session so a user
  // who switches instances still gets the sample project in each one.
  const seededPairsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || !isReady || !user || !instancesLoaded || !currentInstanceId) return;
    const pairKey = `${user.id}::${currentInstanceId}`;
    if (seededPairsRef.current.has(pairKey)) return;
    seededPairsRef.current.add(pairKey);

    let cancelled = false;
    void (async () => {
      // Registered demo environments (e.g. Project Atlas) own their own
      // deterministic baseline. Generic onboarding content must never be
      // seeded into them or it substitutes for the scripted scenario.
      const { data: demoEnv } = await supabase
        .from('demo_environments')
        .select('id')
        .eq('instance_id', currentInstanceId)
        .maybeSingle();
      if (cancelled || demoEnv) return;

      const dailyKey = `mfp_daily_${user.id}_${currentInstanceId}`;
      if (typeof window !== 'undefined' &&
          window.localStorage.getItem(dailyKey) !== '1') {
        await backfillSampleDaily(user.id, currentInstanceId);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(dailyKey, '1');
        }
      }

      const chainKey = `mfp_daychain_${user.id}_${currentInstanceId}`;
      if (typeof window !== 'undefined' &&
          window.localStorage.getItem(chainKey) !== '1') {
        await backfillSampleDayChain(user.id, currentInstanceId);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(chainKey, '1');
        }
      }

      if (typeof window !== 'undefined' &&
          window.localStorage.getItem(lsKey(user.id, currentInstanceId)) === '1') {
        return;
      }
      if (cancelled) return;

      await seedMyFirstProject(user.id, currentInstanceId);
    })();

    return () => { cancelled = true; };
  }, [enabled, isReady, user, instancesLoaded, currentInstanceId]);

}

/**
 * Hook returning a stable `reset()` callback that wipes and re-seeds the
 * current user's sample project in the active instance.
 */
export function useResetFirstProject() {
  const { user } = useAuthReady();
  const { currentInstanceId } = useInstance();

  const reset = useCallback(async () => {
    if (!user || !currentInstanceId) return null;
    return resetMyFirstProject(user.id, currentInstanceId);
  }, [user, currentInstanceId]);

  return { reset, canReset: !!user && !!currentInstanceId };
}
